import { useCallback, useMemo, useState } from 'react'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { sortFontFamiliesByDisplayName } from '@/api/hooks/use-font-families'
import { findFontFamilyById } from '@/lib/fonts/font-loader'
import { useFonts } from '@/providers/font'
import { buildFamilyChangeUpdate, buildStyleNameRemap } from '../helpers/font-family-change'
import type { FontFamilyDisplay } from '../helpers/font-family-display'
import {
  isMixedByStyleName,
  listSharedStyleNames,
  sortVariantsByStyleOrder,
} from '../helpers/font-family-selection'

/** Font Family and Style (variant) state: what's shown, the selection's faces, and applying a
 *  family or style to the current target. */
export function useFontFamily(emitStyleUpdate: (update: TextRunStyleUpdate) => void) {
  const {
    usedFamilies,
    allFamilies,
    ensureFamilyFacesLoaded,
    getFamilyById,
    getVariantByFontId,
    pickDefaultFontIdForFamilyId,
  } = useFonts()

  // Merged set of used + full catalog, replacing the deprecated `families` field.
  const families = useMemo(() => [...usedFamilies, ...allFamilies], [usedFamilies, allFamilies])
  const allFamiliesSorted = useMemo(() => sortFontFamiliesByDisplayName(allFamilies), [allFamilies])

  const [fontFamilyIdValue, setFontFamilyIdValue] = useState<string>('')
  const [fontFamilyIdMixed, setFontFamilyIdMixed] = useState(false)
  const [variantFontIdValue, setVariantFontIdValue] = useState<string>('')
  const [variantFontIdMixed, setVariantFontIdMixed] = useState(false)
  const [variantStyleNamePlaceholder, setVariantStyleNamePlaceholder] = useState<string>('')
  const [mixedSelectionFontIds, setMixedSelectionFontIds] = useState<number[]>([])
  /**
   * Every distinct `fontId` in the current target, **regardless of whether the family is mixed** —
   * the input `buildFamilyChangeUpdate` needs to preserve each run's variant across a family change.
   *
   * Deliberately separate from `mixedSelectionFontIds`, which is reset to `[]` whenever the family is
   * uniform because the Style select only needs it to compare names across *different* families.
   * Reusing it here would have left the remap empty in the single-family/mixed-variant case — i.e.
   * exactly QA's Bug 2 repro (one family, some words bold).
   */
  const [selectionFontIdsAll, setSelectionFontIdsAll] = useState<number[]>([])

  const currentFontDisplayName = useMemo(
    () =>
      fontFamilyIdValue
        ? (allFamiliesSorted.find((f) => String(f.fontFamilyId) === fontFamilyIdValue)
            ?.displayName ?? null)
        : null,
    [fontFamilyIdValue, allFamiliesSorted]
  )

  /**
   * Apply a font family to the current target, keeping each run's own variant — the single path for
   * all three family controls (recent-typeface chip, "Default Font" row, family list). They used to
   * each emit `{ fontFamilyId, fontId: <the family's Regular> }` inline, which flattened every bold
   * and italic run in range to Regular; see `buildFamilyChangeUpdate` for the full mechanism.
   */
  const applyFontFamily = useCallback(
    (fontFamilyId: number, defaultFontId: number) => {
      const { update, resolvedVariantFontId } = buildFamilyChangeUpdate(
        fontFamilyId,
        defaultFontId,
        selectionFontIdsAll,
        getVariantByFontId,
        getFamilyById
      )
      // The family is now uniform, but the VARIANTS still aren't if they weren't before — the whole
      // point of the remap. Snapping the Style field to the resolved face would tell the user their
      // mixed selection had become uniform when it hasn't.
      const variantStillMixed = isMixedByStyleName(selectionFontIdsAll, getVariantByFontId)

      setFontFamilyIdMixed(false)
      setFontFamilyIdValue(String(fontFamilyId))
      setVariantFontIdMixed(variantStillMixed)
      setVariantStyleNamePlaceholder('')
      setVariantFontIdValue(variantStillMixed ? '' : String(resolvedVariantFontId))
      emitStyleUpdate(update)
    },
    [emitStyleUpdate, getFamilyById, getVariantByFontId, selectionFontIdsAll]
  )

  /** Loads the family's faces and applies it with its default face. False when the family has no
   *  default face to apply. */
  const applyFontFamilyById = (fontFamilyId: number): boolean => {
    void ensureFamilyFacesLoaded(fontFamilyId)
    const def = pickDefaultFontIdForFamilyId(fontFamilyId)

    if (def == null) return false
    applyFontFamily(fontFamilyId, def)

    return true
  }

  /** The Style select's change: a face id, or — when families are mixed — a style name. */
  const selectVariant = (v: string) => {
    if (fontFamilyIdMixed) {
      // v is a style name; build per-family fontId mapping
      const fontIdByFontId = buildStyleNameRemap(
        mixedSelectionFontIds,
        v,
        getVariantByFontId,
        getFamilyById
      )

      setVariantFontIdValue(v)
      setVariantFontIdMixed(false)
      emitStyleUpdate({ fontIdByFontId })
    } else {
      setVariantFontIdValue(v)
      emitStyleUpdate({ fontId: Number(v) })
    }
  }

  /** Applies a resolved selection display (see `helpers/font-family-display.ts`). */
  const showFamilyDisplay = useCallback((display: FontFamilyDisplay) => {
    if (display.selectionFontIdsAll !== undefined)
      setSelectionFontIdsAll(display.selectionFontIdsAll)
    if (display.familyIdMixed !== undefined) setFontFamilyIdMixed(display.familyIdMixed)
    if (display.familyIdValue !== undefined) setFontFamilyIdValue(display.familyIdValue)
    if (display.mixedSelectionFontIds !== undefined) {
      setMixedSelectionFontIds(display.mixedSelectionFontIds)
    }

    if (display.variantFontIdMixed !== undefined) setVariantFontIdMixed(display.variantFontIdMixed)
    if (display.variantFontIdValue !== undefined) setVariantFontIdValue(display.variantFontIdValue)
    if (display.variantStyleNamePlaceholder !== undefined) {
      setVariantStyleNamePlaceholder(display.variantStyleNamePlaceholder)
    }
  }, [])

  const selectedFamily = useMemo(() => {
    if (!fontFamilyIdValue) return undefined

    return findFontFamilyById(families, Number(fontFamilyIdValue))
  }, [families, fontFamilyIdValue])

  const sortedVariants = useMemo(
    () => sortVariantsByStyleOrder(selectedFamily?.fonts ?? []),
    [selectedFamily]
  )

  const mixedFamilyStyleItems = useMemo(() => {
    if (!fontFamilyIdMixed || mixedSelectionFontIds.length === 0) return []

    return listSharedStyleNames(mixedSelectionFontIds, getVariantByFontId, getFamilyById)
  }, [fontFamilyIdMixed, mixedSelectionFontIds, getVariantByFontId, getFamilyById])

  return {
    allFamiliesSorted,
    fontFamilyIdValue,
    fontFamilyIdMixed,
    variantFontIdValue,
    variantFontIdMixed,
    variantStyleNamePlaceholder,
    currentFontDisplayName,
    sortedVariants,
    mixedFamilyStyleItems,
    applyFontFamilyById,
    selectVariant,
    showFamilyDisplay,
  }
}
